import { useState, type ReactNode } from "react";
import {
  Button as AriaButton,
  Dialog,
  Modal,
  ModalOverlay,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import { Breadcrumb } from "./Breadcrumb";
import { MobileTopBar } from "./MobileTopBar";
import { SidebarContent } from "./SidebarContent";
import type { NavGroup, NavItem } from "./types";

interface AppShellProps {
  /** Primary nav as labelled groups, already filtered to what THIS user may see. */
  nav: NavGroup[];
  /** Nav pinned above the account row — Settings in the design. */
  footerNav?: NavItem[];
  children: ReactNode;
}

/**
 * Authenticated app chrome: persistent sidebar + content well (Figma 325:23202).
 *
 * Mobile-first (responsive.md): the 320px base is a single column with a top bar and an
 * off-canvas drawer; the 280px permanent sidebar is an ENHANCEMENT added at `lg` (1024px) —
 * which is where the design's desktop frame starts — never a desktop layout walked back with
 * `max-*`. The drawer is a React Aria `Modal`, so it traps focus, closes on Escape, locks
 * background scroll, and restores focus to the trigger; none of that is hand-rolled.
 *
 * Nav arrives as data: core renders chrome, the route layer decides what the chrome contains —
 * including, right now, deciding that there is none (see `routes/-staff-nav.ts`).
 */
export function AppShell({ nav, footerNav, children }: AppShellProps) {
  const { t } = useTranslation("common");
  const [isMenuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-surface-page lg:grid lg:grid-cols-[17.5rem_1fr]">
      {/* Permanent sidebar — 280px, lg and up. */}
      <aside className="hidden border-r border-(--border-default) lg:sticky lg:top-0 lg:block lg:h-screen">
        <SidebarContent groups={nav} footerItems={footerNav} />
      </aside>

      {/* Off-canvas drawer — below lg, anchored to the left edge. */}
      <ModalOverlay
        isOpen={isMenuOpen}
        onOpenChange={setMenuOpen}
        isDismissable
        className="fixed inset-0 z-50 flex bg-overlay entering:animate-in entering:fade-in exiting:animate-out exiting:fade-out lg:hidden"
      >
        <Modal className="h-full w-70 max-w-[85%] shadow-lg">
          <Dialog
            aria-label={t("layout.main-navigation")}
            className="relative h-full outline-none"
          >
            <AriaButton
              onPress={() => setMenuOpen(false)}
              aria-label={t("layout.close-menu")}
              className={
                "absolute top-2.5 right-2 z-10 flex size-11 items-center justify-center rounded-md " +
                "text-(--text-secondary) outline-none transition-colors " +
                "hover:bg-gray-50 pressed:bg-gray-100 " +
                "focus-visible:ring-2 focus-visible:ring-focus-ring"
              }
            >
              <Icon name="close" size={20} />
            </AriaButton>
            <SidebarContent
              groups={nav}
              footerItems={footerNav}
              onNavigate={() => setMenuOpen(false)}
              className="h-full"
            />
          </Dialog>
        </Modal>
      </ModalOverlay>

      {/* `min-w-0` is load-bearing: without it a wide child (a table, a long unbroken string)
          stretches this grid column instead of scrolling inside it. */}
      <div className="flex min-h-screen min-w-0 flex-col">
        <MobileTopBar onOpenMenu={() => setMenuOpen(true)} />
        {/* Design Main (325:23236): 40px padding on all four sides, 24px gap — from lg. */}
        <main className="flex flex-1 flex-col gap-md px-md py-lg sm:px-lg lg:gap-xl lg:p-3xl">
          <Breadcrumb />
          {children}
        </main>
      </div>
    </div>
  );
}
